import { useEffect, useState, useCallback } from "react";
import { Pencil, Trash2, Plus, History } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import TopHeader from "@/components/TopHeader";
import MicRecorder from "@/components/MicRecorder";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
    formatDate, formatHoursShort, utcToBerlinInput, berlinInputToUTC,
} from "@/lib/format";

const ACTIVITIES = [
    "Anfahrt", "Baustelleneinrichtung", "Montage", "Elektroarbeiten",
    "Verkabelung", "Inbetriebnahme", "Fehlersuche", "Nacharbeit",
    "Dokumentation", "Sonstiges",
];

export default function Zeiten() {
    const { user } = useAuth();
    const [entries, setEntries] = useState([]);
    const [projects, setProjects] = useState([]);
    const [users, setUsers] = useState([]);
    const [editEntry, setEditEntry] = useState(null);
    const [historyEntry, setHistoryEntry] = useState(null);
    const [creating, setCreating] = useState(false);

    const load = useCallback(async () => {
        try {
            const [e, p, u] = await Promise.all([
                api.get("/time-entries"),
                api.get("/projects"),
                api.get("/users"),
            ]);
            setEntries(e.data || []); setProjects(p.data || []); setUsers(u.data || []);
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Laden fehlgeschlagen"); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const projectName = (id) => projects.find((p) => p.id === id)?.name || "?";
    const userName = (id) => users.find((u) => u.id === id)?.name || "?";

    const archive = async (id) => {
        if (!confirm("Diesen Eintrag archivieren?")) return;
        try {
            await api.delete(`/time-entries/${id}`);
            toast.success("Archiviert"); load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const grouped = entries.reduce((acc, e) => {
        (acc[e.date] = acc[e.date] || []).push(e); return acc;
    }, {});
    const sortedDates = Object.keys(grouped).sort().reverse();

    return (
        <div>
            <TopHeader title="Zeiten" subtitle={user?.role === "admin" ? "Alle Mitarbeiter" : "Eigene Einträge"} />
            <main className="mx-auto max-w-2xl px-4 pt-4 safe-bottom" data-testid="page-zeiten">
                <div className="mb-3 flex items-center justify-between">
                    <div className="text-[11px] font-medium uppercase tracking-widest text-mes-muted">
                        {entries.length} Einträge
                    </div>
                    <Button data-testid="btn-new-entry" onClick={() => setCreating(true)}
                        className="mes-btn-primary h-10 px-4 text-[13px]">
                        <Plus className="mr-1 h-4 w-4" strokeWidth={2.4} /> Neuer Eintrag
                    </Button>
                </div>

                {sortedDates.length === 0 && (
                    <Card className="mes-card border-dashed p-6 text-center text-sm text-mes-muted">
                        Keine Zeiteinträge vorhanden.
                    </Card>
                )}

                {sortedDates.map((d) => {
                    const daySum = grouped[d].reduce((s, e) => s + (e.duration_minutes || 0), 0);
                    return (
                        <section key={d} className="mb-5" data-testid={`day-group-${d}`}>
                            <div className="mb-1 flex items-baseline justify-between">
                                <div className="font-display text-[13px] font-semibold text-mes-ink">
                                    {formatDate(d + "T00:00:00")}
                                </div>
                                <div className="tabular text-sm font-semibold text-mes-brand">
                                    {formatHoursShort(daySum)}
                                </div>
                            </div>
                            <div className="space-y-2">
                                {grouped[d].map((e) => (
                                    <Card key={e.id} className="mes-card p-3" data-testid={`entry-${e.id}`}>
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="min-w-0 flex-1">
                                                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] font-medium text-mes-muted">
                                                    <span>{userName(e.user_id)}</span>
                                                    <span>·</span>
                                                    <span>{e.activity}</span>
                                                    {e.status === "archived" && <span className="text-mes-danger">Archiviert</span>}
                                                </div>
                                                <div className="mt-0.5 truncate font-semibold text-mes-ink">{projectName(e.project_id)}</div>
                                                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-mes-muted tabular">
                                                    <span>
                                                        {e.start_time ? new Date(e.start_time).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Berlin" }) : "-"}
                                                        {" – "}
                                                        {e.end_time ? new Date(e.end_time).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Berlin" }) : "läuft"}
                                                    </span>
                                                    <span>Pause {e.pause_minutes || 0} min</span>
                                                    <span className="font-semibold text-mes-ink">{formatHoursShort(e.duration_minutes || 0)}</span>
                                                </div>
                                                {e.description && (<div className="mt-1 text-xs text-mes-text line-clamp-2">{e.description}</div>)}
                                            </div>
                                            <div className="flex flex-col gap-1">
                                                {e.corrections?.length > 0 && (
                                                    <Button data-testid={`btn-history-${e.id}`} variant="ghost" size="icon"
                                                        onClick={() => setHistoryEntry(e)} title="Verlauf"
                                                        className="h-8 w-8 text-mes-muted hover:text-mes-ink">
                                                        <History className="h-4 w-4" />
                                                    </Button>
                                                )}
                                                <Button data-testid={`btn-edit-${e.id}`} variant="ghost" size="icon"
                                                    onClick={() => setEditEntry(e)} title="Bearbeiten"
                                                    className="h-8 w-8 text-mes-muted hover:text-mes-ink">
                                                    <Pencil className="h-4 w-4" />
                                                </Button>
                                                {e.status !== "archived" && (
                                                    <Button data-testid={`btn-delete-${e.id}`} variant="ghost" size="icon"
                                                        onClick={() => archive(e.id)} title="Archivieren"
                                                        className="h-8 w-8 text-mes-muted hover:text-mes-danger">
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    </Card>
                                ))}
                            </div>
                        </section>
                    );
                })}
            </main>

            <EntryDialog
                open={!!editEntry || creating}
                mode={editEntry ? "edit" : "create"}
                entry={editEntry}
                projects={projects}
                users={users}
                canPickUser={user?.role === "admin"}
                onClose={() => { setEditEntry(null); setCreating(false); }}
                onSaved={() => { setEditEntry(null); setCreating(false); load(); }}
            />
            <HistoryDialog entry={historyEntry} onClose={() => setHistoryEntry(null)} />
        </div>
    );
}

function EntryDialog({ open, mode, entry, projects, users, canPickUser, onClose, onSaved }) {
    const [projectId, setProjectId] = useState("");
    const [userId, setUserId] = useState("");
    const [activity, setActivity] = useState("Montage");
    const [start, setStart] = useState("");
    const [end, setEnd] = useState("");
    const [pause, setPause] = useState(0);
    const [desc, setDesc] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (mode === "edit" && entry) {
            setProjectId(entry.project_id);
            setUserId(entry.user_id);
            setActivity(entry.activity);
            setStart(utcToBerlinInput(entry.start_time));
            setEnd(utcToBerlinInput(entry.end_time));
            setPause(entry.pause_minutes || 0);
            setDesc(entry.description || "");
        } else if (mode === "create") {
            setProjectId(projects[0]?.id || "");
            setUserId(""); setActivity("Montage");
            const now = new Date();
            const iso = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
            setStart(iso); setEnd(iso); setPause(0); setDesc("");
        }
    }, [mode, entry, projects, open]);

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                project_id: projectId, activity,
                start_time: berlinInputToUTC(start),
                end_time: berlinInputToUTC(end),
                pause_minutes: Math.max(0, Number(pause) || 0),
                description: desc,
            };
            if (mode === "create") {
                if (canPickUser && userId) payload.user_id = userId;
                await api.post("/time-entries", payload);
                toast.success("Eintrag erstellt");
            } else {
                await api.put(`/time-entries/${entry.id}`, payload);
                toast.success("Änderungen gespeichert");
            }
            onSaved();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        setSaving(false);
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent data-testid="entry-dialog" className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>{mode === "edit" ? "Zeiteintrag bearbeiten" : "Neuer Zeiteintrag"}</DialogTitle>
                    <DialogDescription>Zeiten in Europe/Berlin, 24-Stunden.</DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                        <Label>Projekt</Label>
                        <Select value={projectId} onValueChange={setProjectId}>
                            <SelectTrigger data-testid="dialog-project" className="h-11"><SelectValue placeholder="Projekt" /></SelectTrigger>
                            <SelectContent>{projects.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}</SelectContent>
                        </Select>
                    </div>
                    {canPickUser && mode === "create" && (
                        <div className="col-span-2">
                            <Label>Mitarbeiter</Label>
                            <Select value={userId} onValueChange={setUserId}>
                                <SelectTrigger data-testid="dialog-user" className="h-11"><SelectValue placeholder="Für Mitarbeiter (optional)" /></SelectTrigger>
                                <SelectContent>{users.map((u) => (<SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>))}</SelectContent>
                            </Select>
                        </div>
                    )}
                    <div className="col-span-2">
                        <Label>Tätigkeit</Label>
                        <Select value={activity} onValueChange={setActivity}>
                            <SelectTrigger data-testid="dialog-activity" className="h-11"><SelectValue /></SelectTrigger>
                            <SelectContent>{ACTIVITIES.map((a) => (<SelectItem key={a} value={a}>{a}</SelectItem>))}</SelectContent>
                        </Select>
                    </div>
                    <div><Label>Beginn</Label><Input data-testid="dialog-start" type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} className="h-11" /></div>
                    <div><Label>Ende</Label><Input data-testid="dialog-end" type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} className="h-11" /></div>
                    <div><Label>Pause (Min.)</Label><Input data-testid="dialog-pause" type="number" min="0" value={pause} onChange={(e) => setPause(e.target.value)} className="h-11" /></div>
                    <div className="col-span-2">
                        <div className="flex items-center justify-between">
                            <Label>Beschreibung</Label>
                            <MicRecorder testid="mic-desc" onTranscript={(t) => setDesc((v) => (v ? v + " " + t : t))} />
                        </div>
                        <Textarea data-testid="dialog-description" value={desc} onChange={(e) => setDesc(e.target.value)} rows={3} className="mt-1" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} data-testid="dialog-cancel">Abbrechen</Button>
                    <Button onClick={save} disabled={saving} data-testid="dialog-save" className="mes-btn-primary">Speichern</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function HistoryDialog({ entry, onClose }) {
    return (
        <Dialog open={!!entry} onOpenChange={(v) => !v && onClose()}>
            <DialogContent data-testid="history-dialog" className="max-w-lg">
                <DialogHeader><DialogTitle>Änderungsprotokoll</DialogTitle></DialogHeader>
                <div className="max-h-[60vh] overflow-y-auto">
                    {entry?.corrections?.length ? (
                        <ul className="space-y-2 text-sm">
                            {entry.corrections.map((c, i) => (
                                <li key={i} className="rounded-md border border-mes-border p-2">
                                    <div className="text-xs text-mes-muted">
                                        {formatDate(c.at)} {new Date(c.at).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Berlin" })} · {c.user_name}
                                    </div>
                                    <div className="mt-0.5">
                                        <span className="font-semibold">{c.field}</span>:{" "}
                                        <span className="text-mes-muted line-through">{String(c.old)}</span>{" → "}
                                        <span className="font-semibold text-mes-ink">{String(c.new)}</span>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <div className="text-sm text-mes-muted">Keine Änderungen erfasst.</div>
                    )}
                </div>
                <DialogFooter><Button onClick={onClose}>Schließen</Button></DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
